import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Input, InputNumber, message, Row, Space, Switch, Tag, Typography } from 'antd';
import { CloudServerOutlined, SaveOutlined, ThunderboltOutlined } from '@ant-design/icons';
import api from '../services/api';

const { Text } = Typography;

const defaults = {
  enabled: false,
  endpoint: 'https://oss-ap-southeast-1.aliyuncs.com',
  region: 'ap-southeast-1',
  bucket: '',
  access_key_id: '',
  access_key_secret: '',
  custom_domain: '',
  object_prefix: 'w3-videos/',
  sign_expiry_seconds: 3600,
  retention_hours: 24,
};

const W3OSSSettings = () => {
  const [form, setForm] = useState(defaults);
  const [secretSet, setSecretSet] = useState(false);
  const [ready, setReady] = useState(false);
  const [readyError, setReadyError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/w3-oss/config');
      const d = res?.data || {};
      setForm({
        enabled: !!d.enabled,
        endpoint: d.endpoint || defaults.endpoint,
        region: d.region || defaults.region,
        bucket: d.bucket || '',
        access_key_id: d.access_key_id || '',
        access_key_secret: '',
        custom_domain: d.custom_domain || '',
        object_prefix: d.object_prefix || defaults.object_prefix,
        sign_expiry_seconds: d.sign_expiry_seconds || defaults.sign_expiry_seconds,
        retention_hours: d.retention_hours || defaults.retention_hours,
      });
      setSecretSet(!!d.secret_set);
      setReady(!!d.ready);
      setReadyError(d.ready_error || '');
    } catch (e) {
      message.error('加载 OSS 配置失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/w3-oss/config', form);
      message.success(form.enabled ? '配置已保存，新的 W3 任务开始转储' : '配置已保存');
      await load();
    } catch (e) {
      message.error(e?.response?.data?.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const res = await api.post('/admin/w3-oss/test', form);
      if (res?.code === 0) {
        message.success('OSS 连接正常，读写与签名均可用');
      } else {
        message.error(res?.message || '测试失败');
      }
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.error || e?.message || '测试失败';
      message.error(msg);
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card
      title={<span><CloudServerOutlined style={{ marginRight: 8, color: '#1677ff' }} />MuleRouter 转储（阿里云 OSS 新加坡）</span>}
      style={{ marginBottom: 24 }}
      loading={loading}
      extra={ready ? <Tag color="green">配置完整</Tag> : <Tag color="orange">{readyError || '未就绪'}</Tag>}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="私有存储 + 短期签名链接"
        description="W3.0 成片先下载到 OSS 私有 Bucket，用户只拿到签名链接。链接默认 1 小时、文件默认保留 24 小时后自动清理。自定义域名必须 CNAME 到 OSS，若使用 Cloudflare 请设为 DNS Only。"
      />
      <Row gutter={16}>
        <Col span={6}>
          <div style={{ marginBottom: 6 }}>启用转储</div>
          <Switch checked={form.enabled} onChange={v => set('enabled', v)} checkedChildren="开启" unCheckedChildren="关闭" />
        </Col>
        <Col span={9}>
          <div style={{ marginBottom: 6 }}>Endpoint</div>
          <Input value={form.endpoint} onChange={e => set('endpoint', e.target.value)} placeholder="https://oss-ap-southeast-1.aliyuncs.com" />
        </Col>
        <Col span={9}>
          <div style={{ marginBottom: 6 }}>Region</div>
          <Input value={form.region} onChange={e => set('region', e.target.value)} placeholder="ap-southeast-1" />
        </Col>
      </Row>

      <Row gutter={16} style={{ marginTop: 12 }}>
        <Col span={12}>
          <div style={{ marginBottom: 6 }}>Bucket（私有）</div>
          <Input value={form.bucket} onChange={e => set('bucket', e.target.value)} placeholder="your-private-bucket" />
        </Col>
        <Col span={12}>
          <div style={{ marginBottom: 6 }}>对象前缀</div>
          <Input value={form.object_prefix} onChange={e => set('object_prefix', e.target.value)} placeholder="w3-videos/" />
        </Col>
      </Row>

      <Row gutter={16} style={{ marginTop: 12 }}>
        <Col span={12}>
          <div style={{ marginBottom: 6 }}>AccessKey ID</div>
          <Input value={form.access_key_id} onChange={e => set('access_key_id', e.target.value)} placeholder="LTAI..." />
        </Col>
        <Col span={12}>
          <div style={{ marginBottom: 6 }}>AccessKey Secret</div>
          <Input.Password
            value={form.access_key_secret}
            onChange={e => set('access_key_secret', e.target.value)}
            placeholder={secretSet ? '已配置，留空不修改' : '输入 Secret'}
            autoComplete="new-password"
          />
        </Col>
      </Row>

      <Row gutter={16} style={{ marginTop: 12 }}>
        <Col span={12}>
          <div style={{ marginBottom: 6 }}>自定义域名（可空）</div>
          <Input value={form.custom_domain} onChange={e => set('custom_domain', e.target.value)} placeholder="https://media.example.com" />
        </Col>
        <Col span={6}>
          <div style={{ marginBottom: 6 }}>签名有效期（秒）</div>
          <InputNumber min={60} max={86400} value={form.sign_expiry_seconds} onChange={v => set('sign_expiry_seconds', v)} style={{ width: '100%' }} />
        </Col>
        <Col span={6}>
          <div style={{ marginBottom: 6 }}>文件保留期（小时）</div>
          <InputNumber min={1} max={720} value={form.retention_hours} onChange={v => set('retention_hours', v)} style={{ width: '100%' }} />
        </Col>
      </Row>

      <Space style={{ marginTop: 16 }}>
        <Button type="primary" icon={<SaveOutlined />} onClick={save} loading={saving}>保存</Button>
        <Button icon={<ThunderboltOutlined />} onClick={test} loading={testing}>测试连接</Button>
        <Text type="secondary" style={{ fontSize: 12 }}>
          开启后若配置不完整，W3 创建任务会在扣费前返回错误。
        </Text>
      </Space>
    </Card>
  );
};

export default W3OSSSettings;
